import { useRef } from 'react';
import { Link } from 'react-router-dom';
import type { RowPatch, TaskSummary } from '../../lib/tasks';
import { nextTaskStatus, TASK_STATUS_LABELS } from '../../lib/taskStatus';
import { PriorityDot } from '../tasks/TaskBadges';
import { DueDateCell } from '../tasks/DueDatePicker';
import { StatusCell } from '../tasks/StatusPicker';
import { useRevealOnFlash } from '../tasks/useRowFlash';
import { FOCUS_RING_FLUSH } from '../ui';
import { ArrowRightIcon, CheckIcon, TargetIcon } from '../icons';
import { DAY_GRID } from './dayGrid';

/**
 * One task on the dashboard's day list. A sibling of TaskRow rather than a mode
 * of it: the two agree on how a task row behaves and disagree on which columns
 * a cross-project list needs, and a flag threaded through TaskRow to swap its
 * counter cluster for a project name would be harder to read than this.
 *
 * The behaviour it does copy verbatim: the title `<Link>` fills the row via
 * `after:absolute after:inset-0`, and anything else clickable sits at
 * `relative z-10` so its own click does not also follow that link.
 *
 * It also copies TaskRow's pair of trailing controls, and for the same reason.
 * A task on this list is not only ever finished -- it gets picked up, it goes
 * out for review -- and a row whose single control was "done" made completion
 * the only move you could make from the page you actually start the day on.
 *
 * Those two are still one click each for the two moves that dominate. Anything
 * else -- back to To do, straight to In review, and above all "this is stuck" --
 * goes through the same StatusCell the project list uses, which is what stops
 * the leading edge of the row being a dot you can only read.
 *
 * The due date is the same arrangement in the other direction: the third answer
 * to "what about this one" is not a status at all, it is "not today", so that
 * cell is the project list's DueDateCell rather than a printed date.
 *
 * Neither button owns a request: a status change either drops the row off this
 * list or re-sorts it, so a reply it was waiting on would have nowhere to land.
 * The card holds every request and this only reports the intent.
 */
export function DayTaskRow({
  task,
  active,
  inFocus,
  flashing = false,
  onPatch,
  onToggleFocus,
}: {
  task: TaskSummary;
  /** The task this row is for is open in the panel. */
  active: boolean;
  /** Already picked into the current session. */
  inFocus: boolean;
  /** Its due date was just edited and the card moved it -- see useRowFlash. */
  flashing?: boolean;
  onPatch: (patch: RowPatch) => void;
  onToggleFocus: () => void;
}) {
  // Nothing here is ever DONE -- the card filters that status out -- so unlike
  // TaskRow there is no completed state to reconcile with the red, and no
  // reopen half to the ✓ button.
  const next = nextTaskStatus(task.status);
  const rowRef = useRef<HTMLLIElement>(null);
  useRevealOnFlash(rowRef, flashing);

  return (
    <li
      ref={rowRef}
      // The open row is a ring rather than a heavier fill, the same as TaskRow:
      // it spends most of its life under the panel's scrim, where a strong
      // honey reads through 40% black as a muddy olive band.
      className={`${DAY_GRID} group relative px-4 py-2 transition ${
        active ? 'bg-honey-50 ring-1 ring-inset ring-honey-300' : 'hover:bg-stone-50'
      } ${flashing ? 'animate-row-flash' : ''}`}
    >
      {/* The accessible name is the row's full label, not the bare title: a
          screen reader hearing "change the status of on Mac" is the exact
          problem `labelFor` exists for. */}
      <StatusCell
        status={task.status}
        taskTitle={labelFor(task)}
        // Honest: this card has no optimistic rows, so nothing here is ever
        // waiting on a create before it can be edited.
        disabled={false}
        // The cell leads the row here rather than sitting mid-table, so the
        // menu hangs from the left edge -- the default would grow out past the
        // side of the card.
        align="left"
        onPick={(status) => onPatch({ status })}
      />

      {/* `min-w-0` because the grid track is `minmax(0,1fr)`: without it this
          flex row refuses to shrink and pushes the columns beside it off the
          card. Same reason TaskRow's title cell has it. */}
      <span className="flex min-w-0 items-center gap-1">
        {task.parent ? (
          <>
            {/* A breadcrumb rather than TaskRow's bare marker icon: this list
                spans projects and has no tree around it to place a row in, and
                subtask titles are routinely written to be read against their
                parent -- "Fix xyz" with "on Mac" and "on PC" under it. Capped
                at 45% so a long parent cannot crowd out the subtask title,
                which is the half that says what to actually do. */}
            <span className="max-w-[45%] shrink-0 truncate text-sm text-stone-500">
              {task.parent.title}
            </span>
            <span aria-hidden="true" className="shrink-0 text-sm text-stone-400">
              &rsaquo;
            </span>
          </>
        ) : null}

        {/* Opens the panel over the dashboard rather than inside the project.
            Reading a task is not a reason to tear this page down and rebuild it
            somewhere else; the project link beside it is still the way in when
            the project is what you actually wanted. */}
        <Link
          to={`/task/${task.id}`}
          // Spelled out the way TaskRow spells it, so the full parent name is
          // still reachable when either half truncates.
          title={task.parent ? `${task.title} — subtask of ${task.parent.title}` : task.title}
          className="truncate text-sm text-stone-900 after:absolute after:inset-0"
        >
          {task.title}
        </Link>
      </span>

      {/* Its own link, so the project name is a way into the project and not
          just a caption -- hence the z-10 to win against the row link above. */}
      <Link
        to={`/projects/${task.project.id}/tasks`}
        title={task.project.name}
        className="relative z-10 hidden truncate text-xs text-stone-500 hover:text-stone-900 hover:underline sm:block"
      >
        {task.project.name}
      </Link>

      {/*
        The same cell TaskRow uses, so a date is read and changed the same way
        wherever you meet one -- and overdue looks the same here as it does
        inside a project.

        Editable for the reason the status beside it is: this card is the page
        you triage a day on, and "not today" is as ordinary an answer there as
        "done" or "started". Without it the only way to say so was to open the
        task, which is a lot of ceremony for pushing something to tomorrow.

        Kept at phone width, unlike in a project: DAY_GRID has a due track at
        every size, and below `sm:` this and the row's buttons are the whole of
        what the card can edit.

        A date pushed past the window takes the row off the card the same way
        completing it does -- the hook treats both as the row no longer being an
        answer to the question the card asks.
      */}
      <DueDateCell
        task={task}
        taskTitle={labelFor(task)}
        showOnPhone
        // Honest, as above: no optimistic rows on this card, so nothing here is
        // waiting on a create before it can be edited.
        disabled={false}
        onPick={(dueDate) => onPatch({ dueDate })}
      />

      <span className="hidden sm:block">
        <PriorityDot priority={task.priority} />
      </span>

      {/*
        The row's actions, wrapped in one span so the track keeps its shape on a
        row with nowhere further to advance to.

        `invisible`, never `opacity-0`: an `opacity-0` element still hit-tests,
        and these sit inside a row whose `<Link>` covers the whole area, so an
        invisible-but-clickable button at the trailing edge would silently eat
        clicks meant to open the task.

        Hidden only from `sm:` up, and reachable by keyboard the same indirect
        way TaskRow's are: `visibility: hidden` takes an element out of the tab
        order, so tab to the row's title, the `<li>` matches `:focus-within`,
        and the next Tab lands here.
      */}
      <span className="flex items-center justify-end gap-0.5">
        {/*
          Pick this into the current session -- and unpick it, which is why it is
          a toggle rather than a one-way add. Built like PinButton: it owns no
          request, and the click is stopped here so it does not also follow the
          row link underneath.

          A picked row keeps its mark at every width instead of hiding on
          unhover, unlike the two buttons beside it. Those are actions, and an
          action only needs to appear when you reach for it; this one is also a
          *state*, and a session you can only see by hovering each row in turn is
          not one you can see at all.
        */}
        <button
          type="button"
          aria-pressed={inFocus}
          title={inFocus ? 'In this session' : 'Work on this now'}
          aria-label={`${inFocus ? 'Take out of' : 'Add to'} this session: ${labelFor(task)}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggleFocus();
          }}
          className={`relative z-10 flex h-5 w-5 items-center justify-center rounded transition after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] ${
            inFocus
              ? 'text-honey-600 hover:bg-honey-100 hover:text-honey-700'
              : 'text-stone-500 hover:bg-stone-200/70 hover:text-stone-900 sm:invisible sm:group-hover:visible sm:group-focus-within:visible'
          } ${FOCUS_RING_FLUSH}`}
        >
          <TargetIcon className="h-3.5 w-3.5" filled={inFocus} />
        </button>

        {next ? (
          <button
            type="button"
            onClick={() => onPatch({ status: next })}
            title={`Move to ${TASK_STATUS_LABELS[next]}`}
            aria-label={`Move ${labelFor(task)} to ${TASK_STATUS_LABELS[next]}`}
            className={`relative z-10 flex h-5 w-5 items-center justify-center rounded text-stone-500 transition after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] hover:bg-stone-200/70 hover:text-stone-900 sm:invisible sm:group-hover:visible sm:group-focus-within:visible ${FOCUS_RING_FLUSH}`}
          >
            <ArrowRightIcon className="h-3.5 w-3.5" />
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => onPatch({ status: 'DONE' })}
          title="Mark done"
          aria-label={`Mark ${labelFor(task)} done`}
          className={`relative z-10 flex h-5 w-5 items-center justify-center rounded text-stone-500 transition after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] hover:bg-emerald-50 hover:text-emerald-600 sm:invisible sm:group-hover:visible sm:group-focus-within:visible ${FOCUS_RING_FLUSH}`}
        >
          <CheckIcon className="h-3.5 w-3.5" />
        </button>
      </span>
    </li>
  );
}

/** The parent is part of the name on this list for the same reason it is part
 *  of the visible row: "Mark on Mac done" is not a sentence anyone can act on. */
const labelFor = (task: TaskSummary): string =>
  task.parent ? `${task.parent.title}: ${task.title}` : task.title;
